import React, { useMemo } from 'react';
import { Card, Badge, Spinner, Alert } from 'react-bootstrap';
import { useGetCalendricView } from '../api';

const CalendricView = ({ startDate, onDateClick }) => {
  const dates = useMemo(() => {
    const currentMonth = startDate ? new Date(startDate) : new Date();
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    const allDates = [];
    for (let i = 1; i <= totalDays; i++) {
      const d = new Date(year, month, i);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      allDates.push(`${yyyy}-${mm}-${dd}`);
    }
    // Sort ascending to show calendar flow (1-30/31)
    return allDates.sort((a, b) => new Date(a) - new Date(b));
  }, [startDate]);

  const { firstDate, lastDate } = useMemo(() => {
    if (dates.length === 0) return { firstDate: '', lastDate: '' };
    return {
      firstDate: dates[0],
      lastDate: dates[dates.length - 1]
    };
  }, [dates]);

  const { data: apiData, isLoading, error } = useGetCalendricView(
    firstDate && lastDate ? firstDate : null, 
    firstDate && lastDate ? lastDate : null
  );

  const dateCounts = useMemo(() => {
    if (!apiData || !Array.isArray(apiData)) return {};
    
    const counts = {};
    
    // Map API response to our counts structure
    apiData.forEach(item => {
      if (item.date) {
        counts[item.date] = {
          failed: item.failed || 0,
          warning: item.warning || 0,
          unbalanced: item.unbalanced || 0,
          unsynced: item.unsynced || 0,
          successful: item.successful || 0,
          noSales: item.no_sales || 0
        };
      }
    });
    
    return counts;
  }, [apiData]);

  if (isLoading) {
    return (
      <div className="w-100 mx-auto px-4" style={{ maxWidth: '75%' }}>
        <h4 className="mb-3">Calendar View</h4>
        <div className="text-center p-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2 text-muted">Loading calendar data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-100 mx-auto px-4" style={{ maxWidth: '75%' }}>
        <h4 className="mb-3">Calendar View</h4>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p>Failed to load calendar data. Please try again later.</p>
          {process.env.NODE_ENV === 'development' && (
            <details>
              <summary>Error Details</summary>
              <pre>{JSON.stringify(error.response?.data || error.message, null, 2)}</pre>
            </details>
          )}
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-100 mx-auto px-4" style={{ maxWidth: '75%' }}>
      <h4 className="mb-3">Calendar View</h4>
      <div 
        style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
          gap: '15px' 
        }}
      >
        {dates.map(date => {
          const counts = dateCounts[date] || { failed: 0, warning: 0, successful: 0, noSales: 0 };
          const hasData = dateCounts[date] !== undefined;
          const hasPositiveCounts = hasData && (
            counts.failed > 0 ||
            counts.warning > 0 ||
            counts.successful > 0 ||
            counts.noSales > 0
          );
          const isEmpty = !hasData || !hasPositiveCounts;
          
          return (
            <Card key={date} className="h-100 shadow-sm">
              <Card.Header className="text-center p-2 fw-bold bg-body-tertiary" style={{ fontSize: '1rem' }}>
                <button
                  type="button"
                  className="btn btn-link p-0 fw-bold link-body-emphasis"
                  onClick={() => onDateClick?.(date)}
                >
                  {date}
                </button>
              </Card.Header>
              <Card.Body className="p-3 d-flex flex-column justify-content-center">
                {isEmpty ? (
                  <div className="text-center text-muted" style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                    No data available
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {counts.failed > 0 && (
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">Failed</span>
                        <Badge bg="danger" style={{ fontSize: '0.9rem' }}>{counts.failed}</Badge>
                      </div>
                    )}
                    {counts.warning > 0 && (
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">Warning</span>
                        <Badge bg="warning" text="dark" style={{ fontSize: '0.9rem' }}>{counts.warning}</Badge>
                      </div>
                    )}
                    {[['Unbalanced', counts.unbalanced], ['Unsynced', counts.unsynced]].map(([label, n]) => n > 0 && (
                      <div key={label} className="d-flex justify-content-between align-items-center ps-3 small">
                        <span>{label}</span>
                        <Badge bg="warning" text="dark" className="bg-opacity-75">{n}</Badge>
                      </div>
                    ))}
                    {counts.successful > 0 && (
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">Successful</span>
                        <Badge bg="success" style={{ fontSize: '0.9rem' }}>{counts.successful}</Badge>
                      </div>
                    )}
                    {counts.noSales > 0 && (
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">No Sales</span>
                        <Badge bg="secondary" style={{ fontSize: '0.9rem' }}>{counts.noSales}</Badge>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default CalendricView;
